import React from 'react'
import { Typography, Tooltip } from 'antd'
import styled from 'styled-components'
import { DateTime } from 'luxon'

import { colours } from '@qflow/theme'
import { DateFormatDateOnly } from 'portal/lib/functions'
import { InfoCircleOutlined } from '@ant-design/icons'

type ReportTypePanelProps = {
  title: string
  subtitle?: string
  latestPeriod?: string
  latestIssued?: string
  projectTimeZone?: string
  disabled?: boolean
  showComingSoon?: boolean
  info?: string
  onClick?: () => void
}

export const ReportTypePanel = ({
  title,
  subtitle,
  latestPeriod,
  latestIssued,
  projectTimeZone,
  disabled = false,
  showComingSoon = true,
  info,
  onClick
}: ReportTypePanelProps) => {
  const { Title } = Typography

  const formatPeriod = (periodISO: string) => {
    return DateTime.fromISO(periodISO, { zone: 'local' }).toLocaleString(
      {
        timeZone: projectTimeZone,
        year: 'numeric',
        month: 'long'
      },
      { locale: Intl.DateTimeFormat().resolvedOptions().locale }
    )
  }

  const formatIssued = (issuedISO: string) => {
    return DateTime.fromISO(issuedISO, { zone: 'local' }).toLocaleString(
      DateFormatDateOnly(projectTimeZone),
      { locale: Intl.DateTimeFormat().resolvedOptions().locale }
    )
  }

  return (
    <PanelWrapper disabled={disabled} onClick={disabled ? undefined : onClick}>
      <Row>
        <Col>
          <Title level={5}>{title}</Title>
          {subtitle && <Subtitle>{subtitle}</Subtitle>}
        </Col>
        {info && (
          <Col>
            <Tooltip title={info} placement="top" mouseLeaveDelay={0.5}>
              <InfoCircleOutlined />
            </Tooltip>
          </Col>
        )}
      </Row>
      {disabled && showComingSoon && (
        <Row>
          <ComingSoonBadge>Coming soon</ComingSoonBadge>
        </Row>
      )}
      {!disabled && latestPeriod && latestIssued && (
        <Row>
          <Col>
            <LatestLabel>Latest issue</LatestLabel>
            <LatestValue>
              <Period>{formatPeriod(latestPeriod)}</Period> issued{' '}
              {formatIssued(latestIssued)}
            </LatestValue>
          </Col>
        </Row>
      )}
    </PanelWrapper>
  )
}

const PanelWrapper = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-basis: 25%;
  justify-content: space-between;
  padding: 1rem 0.75rem 0.5rem 1rem;
  background-color: ${colours.CONTROL_PANEL_GREY};
  border-radius: 10px;
  gap: 0.5rem;
  min-width: 240px;
  max-width: 25%;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};

  h5 {
    margin-bottom: 0.125rem;
  }

  &:hover {
    background-color: ${({ disabled }) =>
      disabled ? colours.CONTROL_PANEL_GREY : colours.LIGHT_GREY};
  }
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;

  > div {
    margin-bottom: 0.5rem;
  }
`

const Col = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`

const Subtitle = styled.div`
  font-size: 0.875rem;
  color: ${colours.MID_GREY};
  margin-top: 0.125rem;
`

const LatestLabel = styled.div`
  font-size: 0.875rem;
  color: ${colours.MID_GREY};
`

const LatestValue = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
`
const Period = styled.span`
  font-size: 0.9375rem;
  font-weight: 600;
  padding-right: 0.25rem;
`

const ComingSoonBadge = styled.div`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  background-color: ${colours.YELLOW};
  color: ${colours.CORE_NAVY};
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
`
